import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
import './DashboardSkeleton.css';

export interface DashboardWorkspaceSkeletonProps {
  /** The read-only view (filter panel beside the canvas) or the editor (build panel). */
  mode: 'view' | 'editor';
}

/**
 * The widgets of the skeleton's canvas, by shape. Each one is a rectangle on the
 * same twelve-column grid the real group uses (`DashboardWidgetGrid.css`): a row
 * of KPIs, then a wide chart beside a narrow one — the composition most
 * dashboards open with. Sizes live in the stylesheet, as the real card's do.
 */
type WidgetShape = 'kpi' | 'wide' | 'side' | 'half';

const FIRST_GROUP: readonly WidgetShape[] = ['kpi', 'kpi', 'kpi', 'kpi', 'wide', 'side'];
const SECOND_GROUP: readonly WidgetShape[] = ['half', 'half'];

/** Header actions, by width, in the order each mode draws them. */
const ACTIONS: Record<DashboardWorkspaceSkeletonProps['mode'], readonly number[]> = {
  // Read-only tag, Filters, Edit — then Close, which is an icon.
  view: [96, 92, 80],
  // Add widget, Add group, Data sources, Save — then Close.
  editor: [128, 112, 136, 88],
};

const Bone: React.FC<{ className: string; style?: React.CSSProperties }> = ({
  className,
  style,
}) => <span className={`dashboard-skeleton__bone ${className}`} style={style} />;

const WidgetSkeleton: React.FC<{ shape: WidgetShape }> = ({ shape }) => (
  <div className={`dashboard-skeleton__widget dashboard-skeleton__widget--${shape}`}>
    <div className="dashboard-skeleton__widget-head">
      <Bone className="dashboard-skeleton__widget-title" />
    </div>
  </div>
);

const GroupSkeleton: React.FC<{ widgets: readonly WidgetShape[]; titleWidth: number }> = ({
  widgets,
  titleWidth,
}) => (
  <div className="dashboard-skeleton__group">
    <div className="dashboard-skeleton__group-head">
      <Bone className="dashboard-skeleton__group-title" style={{ width: titleWidth }} />
    </div>
    <div className="dashboard-skeleton__grid">
      {widgets.map((shape, index) => (
        <WidgetSkeleton key={index} shape={shape} />
      ))}
    </div>
  </div>
);

/**
 * A dashboard while its chunk — or, on a saved one, its definition — loads, in
 * the shape `DashboardEditorLayout` is about to draw: the header with the name
 * and the actions, the canvas with its groups of widget cards on the twelve-column
 * grid, the side panel (filters in the view, the build panel in the editor) and
 * the page tabs at the foot. One shape from refresh to data: the route's
 * Suspense fallback, `RouteSkeleton` (inside the app chrome) and
 * `SavedDashboardPage` while it waits for the dashboard all draw this.
 *
 * Its own classes, never the layout's: the layout's sheets come with the page's
 * chunk, and this paints before it. No visible text; its accessible name is
 * translated (`loading.aria`). The section's strings are already in the initial
 * bundle — `DashboardErrorBoundary`, imported eagerly by `router.tsx`, loads them.
 */
export const DashboardWorkspaceSkeleton: React.FC<DashboardWorkspaceSkeletonProps> = ({ mode }) => {
  const { t } = useTranslation(NS);

  return (
    <div
      className={`dashboard-skeleton dashboard-skeleton--${mode}`}
      role="status"
      aria-busy="true"
      aria-label={t('loading.aria')}
      data-testid={`dashboard-skeleton-${mode}`}
    >
      <div className="dashboard-skeleton__head" aria-hidden="true">
        <Bone className="dashboard-skeleton__name" />
        <div className="dashboard-skeleton__actions">
          {ACTIONS[mode].map((width, index) => (
            <Bone key={index} className="dashboard-skeleton__action" style={{ width }} />
          ))}
          <Bone className="dashboard-skeleton__action dashboard-skeleton__action--icon" />
        </div>
      </div>

      <div className="dashboard-skeleton__body" aria-hidden="true">
        <div className="dashboard-skeleton__content">
          <GroupSkeleton widgets={FIRST_GROUP} titleWidth={160} />
          <GroupSkeleton widgets={SECOND_GROUP} titleWidth={120} />
        </div>

        <div className="dashboard-skeleton__panel">
          {mode === 'view' ? (
            <>
              <div className="dashboard-skeleton__panel-head">
                <Bone className="dashboard-skeleton__panel-title" />
                <Bone className="dashboard-skeleton__panel-icon" />
              </div>
              <div className="dashboard-skeleton__panel-body">
                <Bone className="dashboard-skeleton__line dashboard-skeleton__line--label" />
                <Bone className="dashboard-skeleton__pill" />
                <Bone className="dashboard-skeleton__pill" />
                <Bone className="dashboard-skeleton__pill" />
              </div>
            </>
          ) : (
            <>
              <div className="dashboard-skeleton__panel-tabs">
                <Bone className="dashboard-skeleton__panel-tab" />
                <Bone className="dashboard-skeleton__panel-tab" />
              </div>
              <div className="dashboard-skeleton__panel-body dashboard-skeleton__panel-body--editor">
                <Bone className="dashboard-skeleton__line dashboard-skeleton__line--label" />
                <Bone className="dashboard-skeleton__field" />
                <Bone className="dashboard-skeleton__line dashboard-skeleton__line--label" />
                <Bone className="dashboard-skeleton__field" />
                <Bone className="dashboard-skeleton__line dashboard-skeleton__line--label" />
                <Bone className="dashboard-skeleton__field dashboard-skeleton__field--tall" />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="dashboard-skeleton__foot" aria-hidden="true">
        <Bone className="dashboard-skeleton__tab" />
        <Bone className="dashboard-skeleton__tab dashboard-skeleton__tab--muted" />
      </div>
    </div>
  );
};
